/**
 * Leonardo Theme Creation & Management
 * 
 * Consolidated theme utilities for creating and managing Leonardo themes
 */

import {
  Theme,
  Color,
  BackgroundColor,
  type CssColor,
  type InterpolationColorspace,
} from '@adobe/leonardo-contrast-colors';
import type { ThemeConfig } from './types';
import {
  DEFAULT_THEME_CONFIG,
  BASE_COLORS,
  BACKGROUND_COLORS,
  LIGHT_MODE_CONFIG,
  DARK_MODE_CONFIG,
  resolveRatiosForTheme,
  getDefaultRatiosForMode,
} from './constants';

/**
 * Resolve interpolation color space from config.
 * Supports both legacy `colorspace` and modern `colorSpace` keys.
 */
const resolveInterpolationColorSpace = (
  config: {
    colorSpace?: InterpolationColorspace;
    colorspace?: InterpolationColorspace;
  }
): InterpolationColorspace => config.colorSpace ?? config.colorspace ?? 'OKLCH';

// =============================================================================
// THEME CREATION
// =============================================================================

/**
 * Create Leonardo theme from configuration
 * 
 * This is the core function that converts our config format into Leonardo's API
 */
export const createLeonardoTheme = (config: ThemeConfig): Theme => {
  // Create background color
  const backgroundColor = new BackgroundColor({
    name: config.backgroundColor.name,
    colorKeys: config.backgroundColor.colorKeys,
    colorSpace: resolveInterpolationColorSpace(config.backgroundColor),
    ratios: config.backgroundColor.ratios,
    smooth: config.backgroundColor.smooth ?? false,
  });

  // Create color scales
  const colors = config.colors
    .filter((colorConfig) => colorConfig.name !== config.backgroundColor.name)
    .map(
      (colorConfig) =>
        new Color({
          name: colorConfig.name,
          colorKeys: colorConfig.colorKeys,
          colorSpace: resolveInterpolationColorSpace(colorConfig),
          ratios: colorConfig.ratios,
          smooth: colorConfig.smooth ?? false,
        })
    );

  // Create theme with Leonardo's API
  const theme = new Theme({
    colors: [backgroundColor, ...colors],
    backgroundColor: backgroundColor,
    lightness: config.lightness,
    contrast: config.contrast,
    saturation: config.saturation,
  });

  return theme;
};

/**
 * Create the default Leonardo theme
 * Uses DEFAULT_THEME_CONFIG from constants
 */
export const createDefaultTheme = (): Theme => {
  return createLeonardoTheme(DEFAULT_THEME_CONFIG);
};

/**
 * Simple theme configuration for runtime updates
 */
export interface SimpleThemeConfig {
  lightness?: number;
  contrast?: number;
  saturation?: number;
  backgroundColor?: 'Black' | 'White';
  baseColors?: {
    black?: string;
    red?: string;
    orange?: string;
    blue?: string;
  };
  formula?: 'wcag2' | 'wcag3';
  ratios?: number[];
  ratiosLight?: number[];
  ratiosDark?: number[];
}

/**
 * Create a theme with custom configuration
 * 
 * This is a simpler API for runtime theme updates that merges
 * the provided config with the default configuration
 * 
 * @example
 * ```typescript
 * const theme = createTheme({ lightness: 50, contrast: 1.5 }, 'dark');
 * ```
 */
export const createTheme = (
  config: Partial<SimpleThemeConfig> = {},
  colorMode: 'light' | 'dark' = 'light'
): Theme => {
  const {
    lightness = 100,
    contrast = 1,
    saturation = 100,
    backgroundColor = 'Black',
    baseColors,
    formula = 'wcag2',
  } = config;

  const ratios = resolveRatiosForTheme(config, colorMode);

  // Use custom base colors if provided, otherwise fall back to defaults
  const colors = {
    black: baseColors?.black ?? BASE_COLORS.black,
    red: baseColors?.red ?? BASE_COLORS.red,
    orange: baseColors?.orange ?? BASE_COLORS.orange,
    blue: baseColors?.blue ?? BASE_COLORS.blue,
  };

  // Create background color using centralized constants
  const bgColor =
    backgroundColor === 'Black'
      ? new BackgroundColor({
          name: 'Black',
          colorKeys: [BACKGROUND_COLORS.dark],
          colorSpace: 'OKLCH',
          ratios: [...ratios],
          smooth: false,
        })
      : new BackgroundColor({
          name: 'White',
          colorKeys: [BACKGROUND_COLORS.light],
          colorSpace: 'OKLCH',
          ratios: [...ratios],
          smooth: false,
        });

  // Create color scales using custom or default base colors
  const gray = new Color({
    name: 'Gray',
    colorKeys: [colors.black as CssColor],
    colorSpace: 'OKLCH',
    ratios: [...ratios],
    smooth: false,
  });

  const red = new Color({
    name: 'Red',
    colorKeys: [colors.red as CssColor],
    colorSpace: 'OKLCH',
    ratios: [...ratios],
    smooth: true,
  });

  const orange = new Color({
    name: 'Orange',
    colorKeys: [colors.orange as CssColor],
    colorSpace: 'OKLCH',
    ratios: [...ratios],
    smooth: false,
  });

  const blue = new Color({
    name: 'Blue',
    colorKeys: [colors.blue as CssColor],
    colorSpace: 'OKLCH',
    ratios: [...ratios],
    smooth: false,
  });

  const theme = new Theme({
    colors: [bgColor, gray, red, orange, blue],
    backgroundColor: bgColor,
    lightness,
    contrast,
    saturation,
    formula,
  });

  return theme;
};

/**
 * Create a static theme (Light or Dark) using custom base colors
 * This allows the "Static" scales to update dynamically with the randomizer
 */
export const createStaticTheme = (
  mode: 'light' | 'dark', 
  baseColors: NonNullable<SimpleThemeConfig['baseColors']>
): Theme => {
  // Use the appropriate template config
  const templateConfig = mode === 'light' ? LIGHT_MODE_CONFIG : DARK_MODE_CONFIG;
  
  // Override colors with provided base colors
  // We need to reconstruct the config because Color objects are immutable in how we use them
  // and we need to swap the colorKeys
  
  const colors = {
    black: baseColors.black ?? BASE_COLORS.black,
    red: baseColors.red ?? BASE_COLORS.red,
    orange: baseColors.orange ?? BASE_COLORS.orange,
    blue: baseColors.blue ?? BASE_COLORS.blue,
  };

  // Create background color
  const bgColor = new BackgroundColor({
    name: templateConfig.backgroundColor.name,
    colorKeys: templateConfig.backgroundColor.colorKeys,
    colorSpace: resolveInterpolationColorSpace(templateConfig.backgroundColor),
    ratios: templateConfig.backgroundColor.ratios,
    smooth: templateConfig.backgroundColor.smooth ?? false,
  });

  // Create scales mapping (neutral scale is named "Black" in ThemeConfig, output name stays "Gray")
  const fallbackRatios = getDefaultRatiosForMode(mode);

  const gray = new Color({
    name: 'Gray',
    colorKeys: [colors.black as CssColor],
    colorSpace: 'OKLCH',
    ratios: templateConfig.colors.find(c => c.name === 'Black')?.ratios ?? fallbackRatios,
    smooth: false,
  });

  const red = new Color({
    name: 'Red',
    colorKeys: [colors.red as CssColor],
    colorSpace: 'OKLCH',
    ratios: templateConfig.colors.find(c => c.name === 'Red')?.ratios ?? fallbackRatios,
    smooth: false,
  });

  const orange = new Color({
    name: 'Orange',
    colorKeys: [colors.orange as CssColor],
    colorSpace: 'OKLCH',
    ratios: templateConfig.colors.find(c => c.name === 'Orange')?.ratios ?? fallbackRatios,
    smooth: false,
  });

  const blue = new Color({
    name: 'Blue',
    colorKeys: [colors.blue as CssColor],
    colorSpace: 'OKLCH',
    ratios: templateConfig.colors.find(c => c.name === 'Blue')?.ratios ?? fallbackRatios,
    smooth: false,
  });

  // Create theme
  return new Theme({
    colors: [bgColor, gray, red, orange, blue],
    backgroundColor: bgColor,
    lightness: templateConfig.lightness,
    contrast: templateConfig.contrast,
    saturation: templateConfig.saturation,
  });
};
